import React, { useState, useRef, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import JobDetailContent from './JobDetailContent'
import { fetchGetJob } from 'src/redux/job'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { updateAiJobFavorite } from 'src/redux/aiJobSearch'
import { useIdentity } from 'pages/Identity/Login'

const JobDetailPage = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { candidateId } = useIdentity()
  const contentRef = useRef(null)

  const rankedJobFromState = location.state?.rankedJob
  const conversationIdFromState = location.state?.conversationId

  const jobFromRedux = useSelector(state => state.job.job)
  const jobLoading = useSelector(state => state.job.loading)

  const [job, setJob] = useState(rankedJobFromState?.job || null)
  const [rankedJob] = useState(rankedJobFromState || null)

  // Fallback: fetch job if no route state (direct URL)
  useEffect(() => {
    if (!rankedJobFromState && id) {
      dispatch(fetchGetJob(id))
    }
  }, [id, rankedJobFromState, dispatch])

  // Update job from Redux when fetched
  useEffect(() => {
    if (!rankedJobFromState && jobFromRedux?.id && String(jobFromRedux.id) === String(id)) {
      setJob(jobFromRedux)
    }
  }, [jobFromRedux, id, rankedJobFromState])

  const handleBack = () => {
    if (location.state?.rankedJob) {
      navigate(-1)
    } else {
      navigate('/jobs')
    }
  }

  const handleFavoriteClick = ({ favorite, jobId }) => {
    dispatch(updateAiJobFavorite({ jobId, favorite }))
    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))

    setJob(previousJob => ({
      ...previousJob,
      candidateJob: {
        ...previousJob.candidateJob,
        favorite,
      },
    }))
  }

  if (!job && jobLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!job) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={handleBack} sx={{ color: 'text.primary' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Job Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography color='text.secondary'>Job not found</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {/* Back header — sticky below the AppBar within the Layout scroll container */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={handleBack} sx={{ color: 'text.primary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {rankedJobFromState ? 'Back to results' : 'Job Details'}
        </Typography>
      </Box>

      {/* Job detail content — scrolls within the Layout container */}
      <Box
        ref={contentRef}
        sx={{ p: 2 }}
      >
        <JobDetailContent
          job={job}
          rankedJob={rankedJob}
          conversationId={conversationIdFromState}
          onFavoriteClick={handleFavoriteClick}
          isMobile
        />
      </Box>
    </Box>
  )
}

export default JobDetailPage
